import { produce } from 'immer';
import { applyChanges, updateZeroStartingPricesByLots } from '@components/bid_request/details/utils';
import { CustomColumnHeaderToClone, EventRequest, LineItemsState, LotChanges, Lot } from 'types';
import { calculateNextLotSno } from './calculateNextLotSno';
import { processAllImportedLots } from './processAllImportedLots';
import { removeEmptyDefaultLotsOnImport } from './removeEmptyDefaultLotsOnImport';
import { shouldRemoveEmptyLotsOnImport } from './shouldRemoveEmptyLotsOnImport';
import { updateRequestPropertiesOnImport } from './updateRequestPropertiesOnImport';

interface ProcessImportConfirmationResultsParams {
  deletedQualifyingCustomColumnHeaders?: CustomColumnHeaderToClone[],
  isReplaceAll?: boolean;
  request?: EventRequest,
  state: LineItemsState
}

/**
 * Processes imported line items and prepares state updates
 *
 * @param props
 * @param props.state The current state
 * @param props.isReplaceAll Whether this is a replace all operation
 * @param props.request The event request object
 * @returns The updated state
 */
export const processImportConfirmationResults = ({
  deletedQualifyingCustomColumnHeaders = [],
  isReplaceAll,
  request,
  state,
}: ProcessImportConfirmationResultsParams): LineItemsState => {
  const { importState, lots } = state;
  const { importedLots, errorLotIndexes } = importState.data;
  const allSurveyQuestions = request?.all_survey_questions || [];

  // Validate there are lots to import
  if (!importedLots || importedLots.length <= errorLotIndexes.length) {
    return produce(state, (draft: LineItemsState): void => {
      const newDraft = draft;

      newDraft.importState.status.inProgress = false;
    });
  }

  return produce(state, (draft: LineItemsState): void => {
    const newDraft = draft;

    // Initialize changes
    if (isReplaceAll) {
      // For replace, reset changes completely
      newDraft.changes = {} as Record<string, LotChanges>;
      newDraft.originalLots.forEach(({ id }: Lot): void => {
        newDraft.changes[id] = { _destroy: true, id } as LotChanges;
      });
    } else {
      // For add, initialize if needed
      newDraft.changes = draft.changes || ({} as Record<string, LotChanges>);
    }

    // If uploading on a new event with no lots
    // Remove the default empty lot that is added
    if (!isReplaceAll && shouldRemoveEmptyLotsOnImport(importState, draft.changes)) {
      newDraft.changes = removeEmptyDefaultLotsOnImport(draft.changes);
    }

    // Get SNO starting point
    const startingSno = calculateNextLotSno(lots);

    const baseImportParams = {
      allSurveyQuestions,
      changes: draft.changes,
      errorLotIndexes,
      importedLots,
      maxSno: startingSno,
      replaceAll: isReplaceAll
    };

    // Create a new object with conditional property
    const importParams = isReplaceAll && deletedQualifyingCustomColumnHeaders && deletedQualifyingCustomColumnHeaders.length > 0
      ? { ...baseImportParams, deletedQualifyingCustomColumnHeaders }
      : baseImportParams;

    const {
      allNewLotQuestions,
      newLots,
      updatedChanges
    } = processAllImportedLots(importParams);

    newDraft.changes = updatedChanges;

    // Determine final lots based on operation type
    let newAllLots;
    if (isReplaceAll || importState.status.uploadStartedFromNewEvent) {
      // Replace or New event: use only new lots
      newAllLots = [...newLots];
    } else {
      // Add: append to existing lots
      newAllLots = [...(lots || []), ...newLots];
    }

    // Combine survey questions
    const newAllSurveyQuestions = [
      ...allSurveyQuestions,
      ...allNewLotQuestions.flat() // Flatten the array of arrays
    ];

    // Calculate the rows whose negotiation starting price is zero
    const zeroStartingPrices = updateZeroStartingPricesByLots(newAllLots);

    // Update request properties
    updateRequestPropertiesOnImport(
      request,
      newAllLots,
      newAllSurveyQuestions,
      newDraft.changes,
      zeroStartingPrices
    );

    newDraft.importState.data.importedLots = []; // Clear imported lots after processing
    newDraft.importState.status.inProgress = false;
    newDraft.importState.status.showLineItemsModal = false;
    newDraft.importState.status.saveLineItemsAfterImport = true;

    newDraft.lots = applyChanges({
      lotChanges: newDraft.changes,
      lots: isReplaceAll ? [] : newDraft.originalLots,
    });

    // Replace-all discards every pre-existing lot, so `originalLots` is dead data — the line above
    // already passes `[]` knowing that. Leaving the stale value in state lets the next action rebuild
    // the view from the pre-import base and resurrect the replaced lot as a second, phantom lot.
    if (isReplaceAll) {
      newDraft.originalLots = [];
    }

    if (isReplaceAll) {
      // start baseline price fetching process after import is complete
      newDraft.baselineState.fetchBaselinesAfterSave = true;
    }
  });
};
